import { ref, computed } from 'vue';
import { VOICE_READER_WS_URL } from '@/assets/constants/api_endpoints';
import {
    WS_REQUEST_READ_PAGE,
    WS_MESSAGE_SENTENCE_AUDIO_READY,
    WS_MESSAGE_READ_PAGE_COMPLETE,
    WS_MESSAGE_VOICE_READER_ERROR,
    VOICE_READER_WEEKLY_LIMIT_MSG,
    VOICE_READER_WEEKLY_LIMIT_UI_MSG,
} from '@/assets/constants/constants';

/**
 * Composable for the voice-reader WebSocket.
 * Manages clip queue, sequential audio playback, and active sentence state.
 */
export function useVoiceReader() {
    const socket = ref(null);

    // Playback state
    const isLoading = ref(false);   // true from read_page send until first clip arrives
    const isPlaying = ref(false);
    const isReading = ref(false);   // true for the entire reading session (loading → playing → done)
    const isPaused = ref(false);    // true when user explicitly paused
    const error = ref(null);
    const isWeeklyLimitReached = ref(false);

    // Active sentence data exposed to parent for highlighting
    const activeSentenceText = ref(null);
    const activeRawSentenceText = ref(null);
    const activePolygonIndices = ref(null);

    // Internal clip queue: Array of { sentence_index, sentence_text, audio_url, text_line_indices }
    const clipQueue = ref([]);
    // Index into clipQueue of the currently-playing clip
    const currentClipIndex = ref(-1);
    // The live HTMLAudioElement
    let currentAudio = null;
    // True when playback stalled because the next clip hasn't arrived yet
    let _waitingForClip = false;

    // True once read_page_complete has been received for the current request
    const pageComplete = ref(false);

    const isActive = computed(() => isLoading.value || isPlaying.value);

    // ─── WebSocket lifecycle ──────────────────────────────────────────────────

    const initializeSocket = (authToken = null) => {
        if (!authToken) {
            console.warn('[VoiceReader] No authentication token provided. WebSocket may fail.');
        }
        let url = VOICE_READER_WS_URL;
        if (authToken) {
            const separator = url.includes('?') ? '&' : '?';
            url = `${url}${separator}token=${authToken}`;
        }
        socket.value = new WebSocket(url);
        _setupSocketHandlers();
    };

    const _setupSocketHandlers = () => {
        if (!socket.value) return;

        socket.value.onmessage = (event) => {
            const data = JSON.parse(event.data);
            _handleMessage(data);
        };

        socket.value.onerror = () => {
            error.value = 'Voice reader connection error.';
            isLoading.value = false;
            isReading.value = false;
            isPaused.value = false;
        };

        socket.value.onclose = () => {
            isLoading.value = false;
        };
    };

    const waitForSocketReady = () => {
        return new Promise((resolve) => {
            if (socket.value && socket.value.readyState === WebSocket.OPEN) {
                resolve();
                return;
            }
            const maxAttempts = 50;
            let attempts = 0;
            const check = setInterval(() => {
                attempts++;
                if (socket.value && socket.value.readyState === WebSocket.OPEN) {
                    clearInterval(check);
                    resolve();
                } else if (attempts >= maxAttempts) {
                    clearInterval(check);
                    resolve();
                }
            }, 100);
        });
    };

    const closeSocket = () => {
        if (socket.value) {
            socket.value.close();
            socket.value = null;
        }
    };

    // ─── Incoming message handler ─────────────────────────────────────────────

    const _handleMessage = (data) => {
        if (data.type === WS_MESSAGE_SENTENCE_AUDIO_READY) {
            isLoading.value = false;
            clipQueue.value.push(data);
            // Auto-start playback as soon as the first clip arrives
            if (currentClipIndex.value === -1) {
                _playClipAt(0);
            } else if (_waitingForClip) {
                // Playback stalled waiting for this clip — resume
                _playClipAt(currentClipIndex.value);
            }
        } else if (data.type === WS_MESSAGE_READ_PAGE_COMPLETE) {
            pageComplete.value = true;
            // If playback already stalled waiting for clips that will never arrive, end session
            if (_waitingForClip) {
                _waitingForClip = false;
                isReading.value = false;
                isPaused.value = false;
            }
        } else if (data.type === WS_MESSAGE_VOICE_READER_ERROR) {
            if (data.message === VOICE_READER_WEEKLY_LIMIT_MSG) {
                error.value = VOICE_READER_WEEKLY_LIMIT_UI_MSG;
            } else {
                error.value = data.message || 'Could not connect. Please refresh and try again.';
            }
            isLoading.value = false;
            isPlaying.value = false;
            isReading.value = false;
            isPaused.value = false;
            if (data.message === VOICE_READER_WEEKLY_LIMIT_MSG) {
                isWeeklyLimitReached.value = true;
            }
        }
    };

    // ─── Playback engine ──────────────────────────────────────────────────────

    const _playClipAt = (index) => {
        if (index >= clipQueue.value.length) {
            currentClipIndex.value = index;
            isPlaying.value = false;
            if (pageComplete.value) {
                // All clips played and server confirmed completion — session is done
                isReading.value = false;
                isPaused.value = false;
            } else {
                // Still waiting for more clips from the server
                _waitingForClip = true;
            }
            return;
        }
        _waitingForClip = false;
        isPaused.value = false;

        const clip = clipQueue.value[index];
        currentClipIndex.value = index;

        // Update highlighting state
        activeSentenceText.value = clip.sentence_text;
        activeRawSentenceText.value = clip.raw_sentence_texts ?? clip.sentence_text;
        activePolygonIndices.value = clip.text_line_indices ?? null;

        // Stop any currently playing audio
        if (currentAudio) {
            currentAudio.pause();
            currentAudio.src = '';
            currentAudio = null;
        }

        const audio = new Audio(clip.audio_url);
        currentAudio = audio;
        isPlaying.value = true;

        audio.play().catch(() => {
            error.value = 'Could not connect. Please refresh and try again.';
            isPlaying.value = false;
        });

        audio.onended = () => {
            _playClipAt(index + 1);
        };
    };

    // ─── Public controls ──────────────────────────────────────────────────────

    /**
     * Request playback for a page. Resets all state first.
     * @param {number} resourceId
     * @param {boolean} isSample
     * @param {number|null} pageNumber  1-based for PDFs, null for other types
     */
    const readPage = async (resourceId, isSample, pageNumber) => {
        if (isWeeklyLimitReached.value && !isSample) return;
        _reset();
        // Set isReading immediately (before async) so the Listen button
        // does not flash back while waiting for the socket to be ready
        isReading.value = true;
        isLoading.value = true;
        isPaused.value = false;
        error.value = null;
        await waitForSocketReady();
        if (!socket.value || socket.value.readyState !== WebSocket.OPEN) {
            error.value = 'Could not connect. Please refresh and try again.';
            isReading.value = false;
            isLoading.value = false;
            return;
        }
        socket.value.send(JSON.stringify({
            type: WS_REQUEST_READ_PAGE,
            resource_id: resourceId,
            is_sample: isSample,
            page_number: pageNumber ?? null,
        }));
    };

    const pause = () => {
        if (currentAudio && isPlaying.value) {
            currentAudio.pause();
            isPlaying.value = false;
            isPaused.value = true;
            _waitingForClip = false;
        }
    };

    const resume = () => {
        if (currentAudio && !isPlaying.value) {
            currentAudio.play().catch(() => {
                error.value = 'Failed to resume audio.';
            });
            isPlaying.value = true;
            isPaused.value = false;
        }
    };

    const stop = () => {
        if (currentAudio) {
            currentAudio.pause();
            currentAudio.src = '';
            currentAudio = null;
        }
        isPlaying.value = false;
        isReading.value = false;
        isPaused.value = false;
        activeSentenceText.value = null;
        activeRawSentenceText.value = null;
        activePolygonIndices.value = null;
    };

    /**
     * Jump to a specific sentence index within the current page's clip queue.
     * @param {number} sentenceIndex  0-based sentence index
     */
    const jumpToSentence = (sentenceIndex) => {
        const idx = clipQueue.value.findIndex(c => c.sentence_index === sentenceIndex);
        if (idx !== -1) {
            _playClipAt(idx);
        }
    };

    // ─── Internal helpers ─────────────────────────────────────────────────────

    const _reset = () => {
        stop();
        clipQueue.value = [];
        currentClipIndex.value = -1;
        pageComplete.value = false;
        isLoading.value = false;
        error.value = null;
    };

    return {
        // State
        isLoading,
        isPlaying,
        isReading,
        isPaused,
        isActive,
        error,
        isWeeklyLimitReached,
        activeSentenceText,
        activeRawSentenceText,
        activePolygonIndices,
        pageComplete,
        // Methods
        initializeSocket,
        waitForSocketReady,
        closeSocket,
        readPage,
        pause,
        resume,
        stop,
        jumpToSentence,
    };
}
